import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";

import { ActionTypes } from "../../../../constants";
import {
    getAppraisalDetails,
    updateAppraisal,
    addAppraisalForm,
    updateAppraisalForm,
    updateSurvey,
} from "../../../../actions/rmsActions";
import { getAppraisalAvatarBadge } from "./AppraisalFunctions";
import {
    getAppraisal,
    getRmsAppCode,
    getError,
    getUser,
} from "../../../../reducers";

import AppraisalDetails from "./AppraisalDetails";
import AppraisalForm from "./AppraisalForm";
import AppraisalProgressDetails from "./AppraisalProgressDetails";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorHandler from "../../../Errors/ErrorHandler";
import FormReviewDialog from "../Form/FormReviewDialog";
import FormSubmitDialog from "../Form/FormSubmitDialog";
import FormStartDialog from "../Form/FormStartDialog";
import FormWrapper from "../Form/FormWrapper";
import RenderDate from "../../../Common/Date/RenderDate";
import RMSAvatar from "../RMSAvatar";
import SignOffDialog from "../SignOff/SignOffDialog";
import SurveyWrapper from "../Form/SurveyWrapper";

import BreadcrumbBack from "../../../Common/Breadcrumbs/BreadcrumbBack";

const canOutputsStart = (outputs) => {
    if (outputs.canStart) {
        return true;
    }

    const segment = outputs?.segments?.find(({ type }) => type === "pdp");
    if (!segment?.items?.length) {
        return false;
    }

    return !!segment.items.find(({ status }) =>
        ["agreed", "deferred", "completed"].includes(status)
    );
};

const Appraisal = ({ match }) => {
    const appCode = useSelector(getRmsAppCode);

    const dispatch = useDispatch();
    const appraisal = useSelector((state) =>
        getAppraisal(match.params.id, state)
    );
    const error = useSelector((state) =>
        getError(state, ActionTypes.GET_RMS_APPRAISAL_DETAILS_SUCCESS)
    );
    const user = useSelector((state) => getUser(state));

    const [openDialog, setOpenDialog] = useState("");
    const [surveyResponses, setSurveyResponses] = useState({});

    const fetchAppraisal = useCallback(() => {
        const { id, appraiseeUUID } = match.params;
        const params = !appraiseeUUID
            ? null
            : { model: "review", appraiseeUUID };
        dispatch(getAppraisalDetails(id, appCode, params));
    }, [dispatch, match.params, appCode]);

    useEffect(fetchAppraisal, [fetchAppraisal, match.params]);

    const location = useLocation();
    const queryString = new URLSearchParams(location.search);
    const refererPath = queryString.get("rp");
    const expandedSegment = !location.hash ? null : location.hash.slice(12);

    if (!!error && (error.errorCode === 404 || error.errorCode === 500)) {
        return (
            <ErrorHandler error={error} breadCrumb={<BreadcrumbBuilder />} />
        );
    }

    const title = !!appraisal?.title?.length ? appraisal.title : "Appraisal";

    const organisationName = appraisal?.organisation?.name ?? "Not specified";
    const status = appraisal?.status ?? "";
    const isCompleted = status === "completed";
    const inputs = appraisal?.inputs ?? null;
    const outputs = appraisal?.outputs ?? null;
    const survey = appraisal?.survey ?? null;
    const dueDate = !!appraisal?.dateDue ? appraisal.dateDue : null;

    const isAppraiser = !!appraisal?.appraiser?.find(
        ({ uuid }) => uuid === user?.uuid
    );

    if (!appraisal) return <LinearProgress />;

    let canUpdate,
        inputsCanStart,
        inputsCanSubmit,
        inputsCanReview,
        outputsDisplay,
        outputsCanStart,
        outputsCanSubmit,
        outputsCanReview,
        surveyDisplay = false;

    switch (status) {
        case "open":
            canUpdate = true;

            if (!isAppraiser && !inputs?.id) {
                inputsCanStart = true;
            }

            switch (inputs?.status) {
                case "incomplete":
                case "rejected":
                case "rejected_1":
                case "rejected_2":
                    inputsCanSubmit = !isAppraiser && appraisal?.meeting?.date;
                    break;

                case "completed":
                    outputsDisplay = true;
                    break;

                default:
                    break;
            }

            break;

        case "review":
            switch (inputs?.status) {
                case "awaiting_approval":
                case "awaiting_approval_1":
                case "awaiting_approval_2":
                    inputsCanReview = isAppraiser;
                    break;

                case "incomplete":
                case "rejected":
                case "rejected_1":
                case "rejected_2":
                    inputsCanSubmit = !isAppraiser && appraisal?.meeting?.date;
                    break;

                case "completed":
                    if (isAppraiser && !outputs?.id) {
                        outputsCanStart = canOutputsStart(outputs);
                    }
                    outputsDisplay = true;
                    break;

                default:
                    break;
            }

            switch (outputs?.status) {
                case "awaiting_approval":
                    outputsCanReview = !isAppraiser;
                    break;

                case "awaiting_approval_2":
                    outputsCanReview = !isAppraiser;
                    break;

                case "incomplete":
                case "rejected":
                case "rejected_1":
                case "rejected_2":
                    outputsCanSubmit = isAppraiser;
                    break;

                case "completed":
                    surveyDisplay = true;
                    break;
                default:
                    break;
            }

            break;

        case "completed":
            outputsDisplay = true;
            surveyDisplay = true;
            break;

        default:
            break;
    }
    inputsCanSubmit = inputsCanSubmit && appraisal?.appraiser?.length > 0;

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    {refererPath?.length > 0 ? (
                        <BreadcrumbBack path={refererPath} />
                    ) : (
                        <BreadcrumbBuilder />
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={
                                <RMSAvatar
                                    type="appraisal"
                                    badge={getAppraisalAvatarBadge(
                                        status,
                                        isAppraiser
                                    )}
                                />
                            }
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    {canUpdate && (
                                        <Button
                                            variant="contained"
                                            color="neutral"
                                            disableElevation={true}
                                            aria-label="edit appraisal"
                                            onClick={() =>
                                                setOpenDialog("update")
                                            }
                                            startIcon={<EditOutlinedIcon />}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </Stack>
                            }
                            title={
                                <Typography
                                    component="h2"
                                    className="card-header-title"
                                >
                                    {title}
                                </Typography>
                            }
                            subheader={
                                <Typography
                                    component="span"
                                    className="card-header-subtitle"
                                >
                                    {!!appraisal?.cycle?.start && (
                                        <RenderDate
                                            date={appraisal.cycle.start}
                                        />
                                    )}
                                    &nbsp;-&nbsp;
                                    {!!appraisal?.cycle?.end && (
                                        <RenderDate
                                            date={appraisal.cycle.end}
                                        />
                                    )}
                                </Typography>
                            }
                        />

                        <CardContent>
                            <Box className="card-content">
                                <AppraisalDetails
                                    appraisee={appraisal?.appraisee}
                                    areaOfWork={appraisal?.areaOfWork}
                                    organisationName={organisationName}
                                    template={
                                        appraisal?.origin?.template ?? "Default"
                                    }
                                    appraiser={appraisal?.appraiser}
                                    meeting={appraisal.meeting}
                                    isCompleted={isCompleted}
                                    isAppraiser={isAppraiser}
                                    dueDate={dueDate}
                                />
                            </Box>
                        </CardContent>

                        <CardContent>
                            <Box className="card-content">
                                <AppraisalProgressDetails
                                    inputs={appraisal.inputs}
                                    outputs={appraisal.outputs}
                                    meeting={appraisal.meeting}
                                    survey={appraisal.survey}
                                    status={appraisal.status}
                                    display="summary"
                                    isAppraiser={isAppraiser}
                                    appraisalId={appraisal.id}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {inputs && (
                    <Grid item xs={12}>
                        <FormWrapper
                            title="Inputs"
                            subheader="This section must be completed prior to the meeting"
                            helperText={appraisal?.inputs?.helperText || ""}
                            appraisalId={appraisal.id}
                            appraiseeUUID={appraisal?.appraisee?.uuid}
                            isAppraiser={isAppraiser}
                            container="inputs"
                            data={inputs}
                            canStart={inputsCanStart}
                            onStart={() => setOpenDialog("inputsStart")}
                            canSubmit={inputsCanSubmit}
                            onSubmit={() => setOpenDialog("inputsSubmit")}
                            canReview={inputsCanReview}
                            onReview={() => setOpenDialog("inputsReview")}
                            onInfo={() => setOpenDialog("inputsInfo")}
                            appCode={appCode}
                            expandedSegment={expandedSegment}
                        />
                    </Grid>
                )}

                {outputsDisplay && outputs && (
                    <Grid item xs={12}>
                        <FormWrapper
                            title="Outputs"
                            subheader="Appraisal summary, including any further professional development."
                            helperText={appraisal?.outputs?.helperText || ""}
                            appraisalId={appraisal.id}
                            appraiseeUUID={appraisal?.appraisee?.uuid}
                            isAppraiser={isAppraiser}
                            container="outputs"
                            data={outputs}
                            canStart={outputsCanStart}
                            onStart={() => setOpenDialog("outputsStart")}
                            canSubmit={outputsCanSubmit}
                            onSubmit={() => setOpenDialog("outputsSubmit")}
                            canReview={outputsCanReview}
                            onReview={() => setOpenDialog("outputsReview")}
                            onInfo={() => setOpenDialog("outputsInfo")}
                            appCode={appCode}
                            expandedSegment={expandedSegment}
                        />
                    </Grid>
                )}

                {surveyDisplay && survey && (
                    <Grid item xs={12}>
                        <SurveyWrapper
                            title="Survey"
                            subheader="Feedback questionnaire"
                            appraisalId={appraisal.id}
                            isAppraiser={isAppraiser}
                            container="survey"
                            data={survey}
                            onSubmit={(params) => {
                                setSurveyResponses(params);
                                setOpenDialog("surveySubmit");
                            }}
                        />
                    </Grid>
                )}
            </Grid>

            {openDialog === "update" && (
                <AppraisalForm
                    open
                    data={appraisal}
                    onSave={(params) =>
                        dispatch(updateAppraisal(appCode, appraisal.id, params))
                    }
                    onClose={() => setOpenDialog("")}
                    formTitle="Edit appraisal"
                    appCode={appCode}
                />
            )}

            {openDialog === "inputsStart" && (
                <FormStartDialog
                    open
                    type="inputs"
                    onSave={() =>
                        dispatch(
                            addAppraisalForm(
                                appCode,
                                appraisal.id,
                                { type: "input" },
                                fetchAppraisal
                            )
                        )
                    }
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "inputsSubmit" && (
                <FormSubmitDialog
                    open
                    type="inputs"
                    onSave={() =>
                        dispatch(
                            updateAppraisalForm(
                                appCode,
                                appraisal.id,
                                inputs?.id,
                                { action: "submit" },
                                fetchAppraisal
                            )
                        )
                    }
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "inputsReview" && (
                <FormReviewDialog
                    open
                    type="inputs"
                    onSave={(params) =>
                        dispatch(
                            updateAppraisalForm(
                                appCode,
                                appraisal.id,
                                inputs?.id,
                                params,
                                fetchAppraisal
                            )
                        )
                    }
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "inputsInfo" && (
                <SignOffDialog
                    open
                    data={appraisal?.inputs?.signOff}
                    resourceType="inputs"
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "outputsStart" && (
                <FormStartDialog
                    open
                    type="outputs"
                    onSave={() =>
                        dispatch(
                            addAppraisalForm(
                                appCode,
                                appraisal.id,
                                { type: "output" },
                                fetchAppraisal
                            )
                        )
                    }
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "outputsSubmit" && (
                <FormSubmitDialog
                    open
                    type="outputs"
                    onSave={() =>
                        dispatch(
                            updateAppraisalForm(
                                appCode,
                                appraisal.id,
                                outputs?.id,
                                { action: "submit" },
                                fetchAppraisal
                            )
                        )
                    }
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "outputsReview" && (
                <FormReviewDialog
                    open
                    type="outputs"
                    onSave={(params) =>
                        dispatch(
                            updateAppraisalForm(
                                appCode,
                                appraisal.id,
                                outputs?.id,
                                params,
                                fetchAppraisal
                            )
                        )
                    }
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "outputsInfo" && (
                <SignOffDialog
                    open
                    data={appraisal?.outputs?.signOff}
                    resourceType="outputs"
                    onClose={() => setOpenDialog("")}
                />
            )}

            {openDialog === "surveySubmit" && (
                <ConfirmDialog
                    open
                    title="Submit survey"
                    content="Submit survey?"
                    confirmButtonLabel="Submit"
                    onCancel={() => setOpenDialog("")}
                    onClose={() => setOpenDialog("")}
                    onConfirm={() =>
                        dispatch(
                            updateSurvey(
                                survey.id,
                                surveyResponses,
                                appCode,
                                fetchAppraisal
                            )
                        )
                    }
                />
            )}
        </Container>
    );
};

export default Appraisal;
